import {
  CardElement as CardElementStripe,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import Billing from '@web-solutions/module-billing';

export const handleSubmitStripe = async({
  formData,
  fields,
  payment,
  handleSubmitError,
  // Reports the failure to the payment processor (error popup + the
  // modal_payment / error_popup / ecommerce error events). `handleSubmitError`
  // only flags the card form as invalid, so without this a declined Stripe
  // charge stayed completely silent.
  onError,
  product,
  onSubmit,
  onSuccess,
  elements,
  accessToken,
}: any) => {
  const valuesAdditionalFields = Object.keys(fields).reduce(
    (acc, key) => ({
      ...acc,
      [key]: fields[key].value,
    }),
    {}
  );

  let sessionRadar: any = {};
  try {
    const { radarSession } = await payment.createRadarSession();
    sessionRadar = radarSession;
  } catch (err) {
    // Radar session creation failed, continue without it
  }

  const cardElement = elements.getElement(CardElementStripe);
  const { first_name, last_name, email } = valuesAdditionalFields as any;

  const billingDetails: any = {};
  if (first_name || last_name) {
    billingDetails.name = `${first_name || ''} ${last_name || ''}`.trim();
  }
  if (email) {
    billingDetails.email = email;
  }

  const { error, paymentMethod } = await payment.createPaymentMethod({
    type: 'card',
    card: cardElement,
    billing_details: billingDetails,
    radar_options: {
      session: sessionRadar?.id || ""
    }
  });

  if (error) {
    // Bad card data stays a form-level error (the field is highlighted); a card
    // error the issuer produced is a payment failure and must be reported.
    if (error.type !== 'validation_error') {
      onError?.(error);
    }
    return handleSubmitError(error);
  }

  // Persist the payment method so upsale one-click purchases can reuse it
  // (POST /stripe/one-time-payment needs { productId, paymentMethodId }).
  if (paymentMethod?.id) {
    (window as any).__stripe_payment_method_id__ = paymentMethod.id;
  }

  console.log('[STRIPE] Product object:', product);

  // For Stripe, get priceId from Stripe API products, not UI product
  const stripeSubscriptionProducts = (window as any).__stripe_subscription_products__;
  const stripeProduct = stripeSubscriptionProducts?.[0];
  const priceId = stripeProduct?.productId;

  console.log('[STRIPE] priceId from Stripe API:', priceId);
  console.log('[STRIPE] paymentMethod.id:', paymentMethod?.id);

  // Get onetime product for the UPSALE flow (GET /stripe/products?type=onetime).
  const onetimeProducts = (window as any).__stripe_onetime_products__;
  const onetimeProductId = onetimeProducts?.[0]?.productId;
  console.log('[STRIPE] onetimeProductId:', onetimeProductId);

  // Subscription-flow trial: charge for the plan the user actually selected
  // (remote-config `products`), not the upsale onetime product.
  // `productId` is the selected product's Stripe product id (prod_...).
  const trialPriceId = product?.productId;
  console.log('[STRIPE] trialPriceId (selected product productId):', trialPriceId);

  try {
    if (accessToken) {
      console.log('[STRIPE] Setting Bearer token for payment requests');
      Billing.setApiAuth(`Bearer ${accessToken}`);
    }

    // Upsale / one-time purchase flow: only create a one-time payment, no subscription.
    // productId comes from the upsale product (GET /stripe/products?type=onetime).
    if (product?.isOneTimePurchase) {
      // productId comes from GET /stripe/products?type=onetime (first product)
      const upsaleProductId = onetimeProductId || product?.id || product?.product;
      console.log('[STRIPE] Creating upsale one-time payment with productId:', upsaleProductId);
      const onetimeResponse = await Billing.createStripeOneTimePayment(upsaleProductId, paymentMethod?.id);
      console.log('[STRIPE] Upsale one-time payment created:', onetimeResponse);

      const oneTime = onetimeResponse as any;
      const purchaseData = {
        ...oneTime,
        transaction_id:
          oneTime.transactionId || oneTime.transaction_id || oneTime.paymentIntentId || oneTime.id,
      };

      if (onSuccess) {
        onSuccess(purchaseData);
      } else {
        onSubmit(purchaseData);
      }
      return;
    }

    // Subscription flow:
    // Step 1: Create one-time payment (trial) for the selected plan's price id.
    if (trialPriceId) {
      console.log('[STRIPE] 1. Creating one-time payment for trial', trialPriceId);
      const onetimeResponse: any = await Billing.createStripeOneTimePayment(trialPriceId, paymentMethod?.id);
      console.log('[STRIPE] 1. One-time payment created:', onetimeResponse);

      // A declined charge can come back as HTTP 200 with an unfinished intent —
      // no throw. Without this check the flow would go on to create the
      // subscription and report a success for a payment that never happened.
      const introStatus = onetimeResponse?.status || onetimeResponse?.payment_intent_status;

      if (introStatus && introStatus !== 'succeeded') {
        const introMessage =
          onetimeResponse?.error?.message ||
          onetimeResponse?.last_payment_error?.message ||
          onetimeResponse?.message;

        throw Object.assign(new Error(introMessage || 'Stripe trial payment was not completed'), {
          code: onetimeResponse?.error?.code || onetimeResponse?.last_payment_error?.code || introStatus,
        });
      }
    }

    // Step 2: Create subscription
    const stripeBody = {
      priceId,
      paymentMethodId: paymentMethod?.id,
      paymentSystem: formData.paymentSystem,
    };

    console.log('[STRIPE] 2. Creating subscription');
    console.log('[STRIPE] 2. Subscription request body:', stripeBody);
    const subscriptionResponse = await Billing.subscribe(stripeBody);
    console.log('[STRIPE] 2. Subscription created:', subscriptionResponse);

    // Map subscriptionId from response to transaction_id for consistency
    const response = subscriptionResponse as any;
    const purchaseData = {
      ...response,
      transaction_id: response.subscriptionId || response.transaction_id,
    };

    if (onSuccess) {
      onSuccess(purchaseData);
    } else {
      onSubmit(purchaseData);
    }
  } catch (error) {
    console.error('[STRIPE] Payment creation error:', error);
    onError?.(error);
    return handleSubmitError(error);
  }
};